import { computed, watch } from 'vue';
import { storeToRefs } from 'pinia';

import { useAiMetadataStore } from '@/modules/viewer/store/useAiMetadataStore';
import { useAiPlaybackStore } from '@/modules/viewer/store/useAiPlaybackStore';
import { useViewerItemStore } from '@/modules/viewer/store/useViewerItemStore';

/**
 * AI Playback (SW-6793): the actual Skip Filler Words / Skip Silences effect
 * on the video element. Split out from useAiPlayback (the AI Actions panel's
 * UI-facing helpers) because this needs to run exactly once per video mount,
 * for both the owner and a guest — Video.vue is the one component shared by
 * both layouts, so it's the only correct place to call this. useAiPlayback
 * itself is owner-only (mounted from the AI Actions panel) and would double
 * up the seeks/skip counts if it also ran this watch.
 *
 * Watches the shared `currentPlaybackTime` that useVideoJs.onTimeUpdate pushes
 * on every video timeupdate/seeked event (the same signal chapters use for
 * active-chapter tracking) and, when the owner's server-persisted setting is
 * on, seeks past any filler word / silence range the play head enters — via
 * `setVideoTime`, the same store-mediated seek chapters already use.
 */
export const useAiPlaybackEffect = () => {
  const aiMetadataStore = useAiMetadataStore();
  const itemStore = useViewerItemStore();
  const playbackStore = useAiPlaybackStore();

  const { fillerWords, silences, skipFillerWords, skipSilences } = storeToRefs(aiMetadataStore);
  const { currentPlaybackTime, videoDuration } = storeToRefs(itemStore);

  // The control bar's remaining-time display can't be remapped to a "virtual"
  // timeline without a much bigger rework (every seek, chapter click, and
  // progress calculation would need to account for the gaps) — but the
  // number it shows can. This is real remaining time minus whatever
  // skippable content is still ahead of the play head, pushed to
  // useVideoJs (which owns the actual video.js component) via the store,
  // the same pattern currentPlaybackTime/setVideoTime already use.
  const remainingSkippableSeconds = computed(() => {
    let total = 0;
    if (skipFillerWords.value) {
      total += (fillerWords.value ?? []).filter(r => r.start >= currentPlaybackTime.value).reduce((sum, r) => sum + (r.end - r.start), 0);
    }
    if (skipSilences.value) {
      total += (silences.value ?? []).filter(r => r.start >= currentPlaybackTime.value).reduce((sum, r) => sum + (r.end - r.start), 0);
    }
    return total;
  });

  const effectiveRemainingSeconds = computed(() => {
    if (!videoDuration.value || (!skipFillerWords.value && !skipSilences.value)) {
      return null;
    }
    return Math.max(0, videoDuration.value - currentPlaybackTime.value - remainingSkippableSeconds.value);
  });

  watch(effectiveRemainingSeconds, seconds => itemStore.setAiPlaybackEffectiveRemainingSeconds(seconds), { immediate: true });

  // Ranges already skipped this mount, keyed by start time. Cleared on a
  // backward seek (see below) so rewinding and replaying a section skips it
  // again instead of silently playing through it as "already consumed".
  let consumedFillerWordStarts = new Set<number>();
  let consumedSilenceStarts = new Set<number>();

  // `timeupdate` fires only a few times a second, with a bigger gap than many
  // filler-word/silence ranges are wide — a plain "is `time` inside a range"
  // check can land on either side of a short range between two ticks and
  // never see it. Instead, track how far the play head has moved since the
  // last tick and check whether any unconsumed range was crossed in that
  // span, not just whether we happen to be inside one right now.
  // Both filler-word and silence checks advance this together on every tick
  // (there's only one play head), so a single shared position is enough.
  //
  // The very first `from` can't just be `currentPlaybackTime.value` read here
  // at setup: a remount mid-video (persisted toggle already on) already has
  // it pre-seeded, but a `?t=N` deep link doesn't — that seek happens later,
  // asynchronously, in useVideoJs, so `currentPlaybackTime` is still 0 at
  // this line even though playback will jump straight to N. `initialVideoTime`
  // is set synchronously ahead of that seek, so by the time the first real
  // tick below fires it already reflects N. Take whichever of the two is
  // bigger as the real starting point, so neither case treats an unplayed
  // span as freshly played through.
  const mountedPlaybackTime = currentPlaybackTime.value;
  let lastCheckTime: number | null = null;

  watch(currentPlaybackTime, time => {
    const from = lastCheckTime ?? Math.max(mountedPlaybackTime, itemStore.initialVideoTime);
    lastCheckTime = time;

    if (time < from) {
      // Rewound past ranges already marked consumed — let replaying that
      // section skip them again instead of playing through silently.
      consumedFillerWordStarts = new Set();
      consumedSilenceStarts = new Set();
      return;
    }

    // Both kinds of range can be crossed in the same tick (e.g. a silence
    // immediately followed by a filler word) — fold every crossed end into
    // one seek rather than issuing one per kind, or the second setVideoTime
    // call overwrites the first's target and the first range plays through
    // despite already being marked consumed.
    const crossedEnds: number[] = [];

    if (skipFillerWords.value) {
      const crossed = (fillerWords.value ?? []).filter(r => !consumedFillerWordStarts.has(r.start) && r.start < time && r.end > from);
      crossed.forEach(r => {
        consumedFillerWordStarts.add(r.start);
        playbackStore.recordFillerWordSkipped();
        crossedEnds.push(r.end);
      });
    }

    if (skipSilences.value) {
      const crossed = (silences.value ?? []).filter(r => !consumedSilenceStarts.has(r.start) && r.start < time && r.end > from);
      if (crossed.length > 0) {
        let secondsSkipped = 0;
        crossed.forEach(r => {
          consumedSilenceStarts.add(r.start);
          secondsSkipped += r.end - r.start;
          crossedEnds.push(r.end);
        });
        playbackStore.recordSilenceSkipped(secondsSkipped);
      }
    }

    if (crossedEnds.length > 0) {
      itemStore.setVideoTime(Math.max(time, ...crossedEnds), { autoplay: true });
    }
  });
};
